import type {
  ItemTransform,
  Position2D,
} from '@Components/BigBrowse/Model/ModelTypes';
import { useBbContentPresenter } from '@Components/BigBrowse/bb2/BbContentPresenter';
import type { SpringValue } from '@react-spring/three';
import { useSpring } from '@react-spring/three';
import { computed } from 'mobx';
import { useRef } from 'react';

export function useItemTransformAnimations(
  uniqueKey: string,
  position: Position2D,
  scaleFactor = 1
): {
  x: SpringValue<number>;
  y: SpringValue<number>;
  scale: SpringValue<number>;
} {
  const itemTransforms = useItemTransforms(uniqueKey);

  const { xAnim } = useSpring({
    to: { xAnim: position.x + (itemTransforms?.position?.to.x ?? 0) },
    config: { duration: 1000 },
  });

  const { yAnim } = useSpring({
    ...(itemTransforms?.position?.from
      ? { from: { yAnim: position.y + itemTransforms?.position?.from.y } }
      : {}),
    to: { yAnim: position.y + (itemTransforms?.position?.to.y ?? 0) },
    config: { duration: 1000 },
  });

  const combinedScale = scaleFactor * (itemTransforms?.scale?.to ?? 1);

  const { scaleAnim } = useSpring({
    to: { scaleAnim: combinedScale },
    config: { duration: 500 },
  });

  return { x: xAnim, y: yAnim, scale: scaleAnim };
}

export function useItemTransforms(
  uniqueKey: string
): ItemTransform | undefined {
  const bbContentPresenter = useBbContentPresenter();

  return computed(() => bbContentPresenter.itemTransforms.get(uniqueKey)).get();
}

export function useDebouncedClick(callback?: Function) {
  const bbContentPresenter = useBbContentPresenter();

  const { onPointerDown, onPointerUp, getPanDistance } =
    bbContentPresenter.canvasManager;

  const clickRef = useRef(new Date().valueOf());

  const onClick = () => {
    if (getPanDistance() >= 10) {
      // We allow a small distance to be considered as no Pan
      return;
    }

    const now = new Date().valueOf();
    if (now - clickRef.current < 500) {
      // We avoid accidental double-taps
      return;
    }

    clickRef.current = new Date().valueOf();
    callback?.();
  };

  return { onPointerUp, onPointerDown, onClick };
}
